import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Square, Circle, MoveUpRight, Type, Undo2, Trash2, Check, X } from 'lucide-react';
import { fitJpegUnderLimit } from '../lib/reportImages';

const COLORS = [
    { value: '#EF4444', label: 'Red' },
    { value: '#3B82F6', label: 'Blue' },
    { value: '#EAB308', label: 'Yellow' },
    { value: '#22C55E', label: 'Green' },
];

const TOOLS = [
    { id: 'rect', icon: Square, label: 'Rectangle' },
    { id: 'circle', icon: Circle, label: 'Circle' },
    { id: 'arrow', icon: MoveUpRight, label: 'Arrow' },
    { id: 'text', icon: Type, label: 'Text' },
];

function drawArrowhead(ctx, x1, y1, x2, y2, size = 12) {
    const angle = Math.atan2(y2 - y1, x2 - x1);
    ctx.beginPath();
    ctx.moveTo(x2, y2);
    ctx.lineTo(x2 - size * Math.cos(angle - Math.PI / 6), y2 - size * Math.sin(angle - Math.PI / 6));
    ctx.moveTo(x2, y2);
    ctx.lineTo(x2 - size * Math.cos(angle + Math.PI / 6), y2 - size * Math.sin(angle + Math.PI / 6));
    ctx.stroke();
}

export default function AnnotationCanvas({ screenshotDataUrl, onComplete, onCancel }) {
    const canvasRef = useRef(null);
    const imageRef = useRef(null);
    const [tool, setTool] = useState('rect');
    const [color, setColor] = useState('#EF4444');
    const [annotations, setAnnotations] = useState([]);
    const [drawing, setDrawing] = useState(false);
    const [startPos, setStartPos] = useState(null);
    const [currentPos, setCurrentPos] = useState(null);
    const [textInput, setTextInput] = useState(null); // { canvasX, canvasY, screenX, screenY }
    const [textValue, setTextValue] = useState('');
    const textInputRef = useRef(null);
    const containerRef = useRef(null);

    // Load image. Done waits for it: before, the canvas is a blank 300×150 and
    // would replace the image with an empty one (a large attached PNG, #932).
    const [loaded, setLoaded] = useState(false);
    useEffect(() => {
        setLoaded(false);
        const img = new Image();
        img.onload = () => {
            imageRef.current = img;
            const canvas = canvasRef.current;
            if (canvas) {
                canvas.width = img.width;
                canvas.height = img.height;
                redraw([]);
                setLoaded(true);
            }
        };
        img.src = screenshotDataUrl;
    }, [screenshotDataUrl]);

    const redraw = useCallback((anns) => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        const img = imageRef.current;
        if (!ctx || !img) return;

        // White under the image: Done exports JPEG, which has no transparency, so
        // a transparent PNG's clear areas would otherwise turn black.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);

        for (const ann of anns) {
            ctx.strokeStyle = ann.color;
            ctx.fillStyle = ann.color;
            ctx.lineWidth = 3;
            ctx.font = 'bold 18px Inter, Arial, sans-serif';

            if (ann.type === 'rect') {
                ctx.strokeRect(ann.x, ann.y, ann.w, ann.h);
            } else if (ann.type === 'circle') {
                ctx.beginPath();
                ctx.ellipse(ann.cx, ann.cy, Math.abs(ann.rx), Math.abs(ann.ry), 0, 0, 2 * Math.PI);
                ctx.stroke();
            } else if (ann.type === 'arrow') {
                ctx.beginPath();
                ctx.moveTo(ann.x1, ann.y1);
                ctx.lineTo(ann.x2, ann.y2);
                ctx.stroke();
                drawArrowhead(ctx, ann.x1, ann.y1, ann.x2, ann.y2);
            } else if (ann.type === 'text') {
                // Background pill
                const metrics = ctx.measureText(ann.text);
                const padding = 6;
                ctx.fillStyle = 'rgba(0,0,0,0.65)';
                ctx.fillRect(ann.x - padding, ann.y - 18, metrics.width + padding * 2, 24);
                ctx.fillStyle = '#fff';
                ctx.fillText(ann.text, ann.x, ann.y);
            }
        }
    }, []);

    // Redraw when annotations change
    useEffect(() => { redraw(annotations); }, [annotations, redraw]);

    // Esc cancels the editor, like the report dialog it opened from. In the text
    // box, Esc only drops the text: its handler stops the event, and by the time
    // it would reach here that box has already unmounted.
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape' && e.target?.tagName !== 'INPUT') onCancel();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onCancel]);

    // Get position relative to canvas
    const getPos = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
    };

    const handlePointerDown = (e) => {
        if (tool === 'text') {
            const canvasPos = getPos(e);
            // Get screen-relative position for the input overlay
            const canvasRect = canvasRef.current.getBoundingClientRect();
            const containerRect = containerRef.current?.getBoundingClientRect() || canvasRect;
            const screenX = e.clientX - containerRect.left;
            const screenY = e.clientY - containerRect.top;
            setTextInput({ canvasX: canvasPos.x, canvasY: canvasPos.y, screenX, screenY });
            setTextValue('');
            setTimeout(() => textInputRef.current?.focus(), 100);
            return;
        }
        const pos = getPos(e);
        setStartPos(pos);
        setCurrentPos(pos);
        setDrawing(true);
    };

    const handlePointerMove = (e) => {
        if (!drawing || !startPos) return;
        const pos = getPos(e);
        setCurrentPos(pos);

        // Draw preview
        redraw(annotations);
        const ctx = canvasRef.current?.getContext('2d');
        if (!ctx) return;
        ctx.strokeStyle = color;
        ctx.lineWidth = 3;

        if (tool === 'rect') {
            ctx.strokeRect(startPos.x, startPos.y, pos.x - startPos.x, pos.y - startPos.y);
        } else if (tool === 'circle') {
            const cx = (startPos.x + pos.x) / 2;
            const cy = (startPos.y + pos.y) / 2;
            const rx = Math.abs(pos.x - startPos.x) / 2;
            const ry = Math.abs(pos.y - startPos.y) / 2;
            ctx.beginPath();
            ctx.ellipse(cx, cy, rx, ry, 0, 0, 2 * Math.PI);
            ctx.stroke();
        } else if (tool === 'arrow') {
            ctx.beginPath();
            ctx.moveTo(startPos.x, startPos.y);
            ctx.lineTo(pos.x, pos.y);
            ctx.stroke();
            drawArrowhead(ctx, startPos.x, startPos.y, pos.x, pos.y);
        }
    };

    const handlePointerUp = () => {
        if (!drawing || !startPos || !currentPos) { setDrawing(false); return; }

        let ann = null;
        if (tool === 'rect') {
            ann = { type: 'rect', x: startPos.x, y: startPos.y, w: currentPos.x - startPos.x, h: currentPos.y - startPos.y, color };
        } else if (tool === 'circle') {
            ann = { type: 'circle', cx: (startPos.x + currentPos.x) / 2, cy: (startPos.y + currentPos.y) / 2, rx: Math.abs(currentPos.x - startPos.x) / 2, ry: Math.abs(currentPos.y - startPos.y) / 2, color };
        } else if (tool === 'arrow') {
            ann = { type: 'arrow', x1: startPos.x, y1: startPos.y, x2: currentPos.x, y2: currentPos.y, color };
        }

        if (ann) setAnnotations(a => [...a, ann]);
        setDrawing(false);
        setStartPos(null);
        setCurrentPos(null);
    };

    const commitText = () => {
        if (textValue.trim() && textInput) {
            setAnnotations(a => [...a, { type: 'text', x: textInput.canvasX, y: textInput.canvasY, text: textValue.trim(), color }]);
        }
        setTextInput(null);
        setTextValue('');
    };

    const undo = () => setAnnotations(a => a.slice(0, -1));
    const clearAll = () => setAnnotations([]);

    const handleDone = () => {
        redraw(annotations); // Ensure final state
        const canvas = canvasRef.current;
        // JPEG under the 5 MB report limit (#932); the old 2.8 M-character rule
        // left a large attached photo over it.
        onComplete(fitJpegUnderLimit(canvas));
    };

    // `!mt-0`: on Feature Requests, BugReportModal's fragment puts this in the
    // page root's `space-y-5`, whose margin would push it 20px down (#496).
    return (
        <div className="fixed inset-0 z-[60] !mt-0 bg-black/80 flex flex-col" data-report-ui="annotator"
            role="dialog" aria-modal="true" aria-label="Annotate screenshot">
            {/* Toolbar */}
            <div className="bg-white/95 backdrop-blur border-b border-stone-200 px-4 py-2 flex items-center gap-3 flex-shrink-0">
                <span className="text-sm font-semibold text-stone-700 mr-2">Annotate Screenshot</span>

                <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-0.5">
                    {TOOLS.map(t => {
                        const Icon = t.icon;
                        return (
                            <button key={t.id} onClick={() => setTool(t.id)} title={t.label}
                                className={`p-2 rounded-md transition-colors ${tool === t.id ? 'bg-stone-900 text-white' : 'text-stone-500 hover:text-stone-700'}`}>
                                <Icon className="w-4 h-4" />
                            </button>
                        );
                    })}
                </div>

                <div className="w-px h-6 bg-stone-200" />

                <div className="flex items-center gap-1">
                    {COLORS.map(c => (
                        <button key={c.value} onClick={() => setColor(c.value)} title={c.label}
                            className={`w-6 h-6 rounded-full border-2 transition-all ${color === c.value ? 'border-stone-900 scale-110' : 'border-transparent'}`}
                            style={{ backgroundColor: c.value }} />
                    ))}
                </div>

                <div className="w-px h-6 bg-stone-200" />

                <button onClick={undo} disabled={annotations.length === 0} title="Undo"
                    className="p-2 text-stone-500 hover:text-stone-700 disabled:opacity-30 transition-colors">
                    <Undo2 className="w-4 h-4" />
                </button>
                <button onClick={clearAll} disabled={annotations.length === 0} title="Clear all"
                    className="p-2 text-stone-500 hover:text-red-500 disabled:opacity-30 transition-colors">
                    <Trash2 className="w-4 h-4" />
                </button>

                <div className="flex-1" />

                <button onClick={onCancel} className="px-3 py-1.5 text-sm text-stone-500 hover:text-stone-700">
                    Cancel
                </button>
                <button onClick={handleDone} disabled={!loaded}
                    className="px-4 py-1.5 text-sm bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50 disabled:cursor-wait flex items-center gap-1.5 font-medium">
                    <Check className="w-4 h-4" /> Done
                </button>
            </div>

            {/* Canvas */}
            <div className="flex-1 overflow-auto flex items-center justify-center p-4 relative" ref={containerRef}>
                <div className="relative inline-block">
                    <canvas ref={canvasRef}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        className="max-w-full max-h-[calc(100vh-80px)] rounded-lg shadow-2xl cursor-crosshair touch-none block"
                        style={{ imageRendering: 'auto' }} />
                </div>

                {/* Text input — positioned absolutely in the container using screen coords */}
                {textInput && (
                    <input ref={textInputRef} value={textValue}
                        onChange={e => setTextValue(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') { e.preventDefault(); commitText(); }
                            if (e.key === 'Escape') { e.stopPropagation(); setTextInput(null); setTextValue(''); }
                        }}
                        onBlur={() => setTimeout(commitText, 150)}
                        className="absolute px-2 py-1.5 text-sm font-medium border-2 rounded-lg bg-white shadow-xl min-w-[180px] z-20 outline-none"
                        style={{
                            left: textInput.screenX,
                            top: textInput.screenY,
                            borderColor: color,
                            color: color,
                        }}
                        placeholder="Type annotation..." />
                )}
            </div>
        </div>
    );
}
